import * as React from "react";

import Typography from "@material-ui/core/Typography";

// jaza-soft
import { translate } from "jazasoft";

import MuiTable from "../../../../components/MuiTable";

import { getDistinctValues, isEmpty, toFixed } from "../../../../utils/helpers";

import CardHeader from "../../../../components/CardHeader";
import { useDeepMemo, useSetting } from "../../../../hooks";
// import { useDeepMemo, useEntityPrivilege, useSetting } from "../../../../hooks";

import usePoView from "./usePoView";
import { Entity } from "../../../../utils/types";
import SettingKeys from "../../../../utils/SettingKeys";
// import hasPrivilege from "../../../../utils/hasPrivilege";
// import { ResourceId, Scope } from "../../../../utils/privilege";
import { UOM } from "../../../../constants";
import RollInventory from "./RollInventory";
import { convert } from "../../../../helpers/unitConversionHelper";

const UomCodeFromValue = Object.values(UOM).reduce((acc, uom) => ({ ...acc, [uom.value]: uom.code }), {});

const FabricQualityView = props => {
  const { roles, hasAccess, history } = props;

  // const { disableEdit: disableAssociation } = useEntityPrivilege("Ledger");

  const {
    order,
    selectedKey,
    flowInfoColumns,
    flowInfoRows,
    ledgerList,
    rollList,
    part,
    flowInfoList,
    allWhList,
    whList,
    onRefresh,
    onRowClick,
    updateAssociation
  } = usePoView(props);

  const invoiceFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.INVOICES);
  const fabricItemFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.FABRIC_ITEMS);
  const fabricFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.FABRICS);
  const conversionFactor = useSetting(SettingKeys.UOM_CONVERSION_FACTOR);
  const conversionFactorList = JSON.parse(conversionFactor || "[]");

  const selectedRow = flowInfoRows.find(e => e.key === selectedKey);
  const parentKey = selectedRow?.parentKey;

  const associationRows = useDeepMemo(() => {
    if (isEmpty(ledgerList)) return [];
    const whCodes = whList.map(e => e.code);
    const rows = ledgerList
      .map(ledger => {
        const allocatedRollList = rollList?.filter(r => !isEmpty(r.ledgerOut) && r.ledgerOut?.id === ledger.id) || [];
        const allocatedQty = allocatedRollList.reduce((acc, roll) => acc + roll.length, 0);
        const allocatedWeight = allocatedRollList.reduce((acc, roll) => acc + (roll.weight || 0), 0);
        const finalAllocatedQty =
          ledger.fabricItem?.uomLength === UOM.KILOGRAM.value
            ? allocatedWeight
            : convert({
                input: allocatedQty,
                uomIn: UOM.METER.value,
                uomOut: ledger.fabricItem?.uomLength,
                gsm: ledger.fabricItem?.orderingGsm,
                widthInCm: ledger.fabricItem?.width,
                conversionFactorList
              });
        return {
          id: ledger.id,
          whCode: ledger.fabricItem?.warehouse,
          warehouse: allWhList.find(e => e.code === ledger.fabricItem?.warehouse)?.name,
          invoiceNo: ledger.fabricItem?.invoice?.invoiceNo,
          invoiceId: ledger.fabricItem?.invoice?.id,
          erpInvoiceNo: ledger.fabricItem?.invoice?.erpInvoiceNo,
          fabricId: ledger.fabric?.id,
          itemCode: ledger.fabric?.itemCode,
          itemDesc: ledger.fabric?.itemDesc,
          articleName: ledger.fabric?.articleName,
          construction: ledger.fabric?.construction,
          content: ledger.fabric?.content,
          color: ledger?.fabColor,
          colorShade: ledger.fabricItem?.colorShade,
          rollForm: ledger.fabricItem?.rollForm,
          fpo: ledger.fabricItem?.fpo,
          grn: ledger.fabricItem?.grn,
          grnQty: ledger.fabricItem?.grnQty,
          orderingWidth: ledger.fabricItem?.orderingWidth,
          uomWidth: ledger.fabricItem?.uomWidth,
          uomLength: ledger.fabricItem?.uomLength,
          associatedQty: ledger.ledgerQty,
          allocatedQty: toFixed(finalAllocatedQty)
        };
      })
      .filter(e => isEmpty(whCodes) || whCodes.includes(e.whCode));
    const uomLengthList = getDistinctValues(rows.map(e => e.uomLength));
    if (rows?.length > 1) {
      const totalRow = ["availableQty", "associatedQty", "allocatedQty", "grnQty"].reduce(
        (acc, dataKey) => ({ ...acc, [dataKey]: rows.reduce((acc, row) => acc + (row[dataKey] || 0), 0) }),
        { color: "Total", uomLength: uomLengthList?.length === 1 ? uomLengthList[0] : "", totalRow: true }
      );
      return [...rows, totalRow];
    }
    return rows;
  }, [ledgerList, rollList, allWhList, whList, conversionFactorList]);

  const associationColumns = useDeepMemo(() => {
    let fields = [...invoiceFields, ...fabricFields, ...fabricItemFields, "associatedQty", "allocatedQty"];
    if (allWhList?.length > 1) {
      fields.push("warehouse");
    }
    const columns = [
      { dataKey: "grn", title: "GRN", render: (value, shortValue) => shortValue || "-" },
      { dataKey: "invoiceNo", title: "Invoice No", linkPath: row => history.push(`/warehouse/invoices/${row.invoiceId}/view`) },
      { dataKey: "erpInvoiceNo", title: "ERP Invoice No", render: (value, shortValue) => shortValue || "-" },
      { dataKey: "warehouse", title: "Warehouse" },
      { dataKey: "itemCode", title: "Item Code", linkPath: row => history.push(`/warehouse/fabrics/${row.fabricId}/view`) },
      { dataKey: "color", title: "Fabric Color" },
      { dataKey: "itemDesc", title: "Item Description", render: (value, shortValue) => shortValue || "-" },
      { dataKey: "articleName", title: "Article Name", render: (value, shortValue) => shortValue || "-" },
      { dataKey: "construction", title: "Count Construction", render: (value, shortValue) => shortValue || "-" },
      { dataKey: "content", title: "Fabric Content", render: (value, shortValue) => shortValue || "-" },
      { dataKey: "colorShade", title: "Color Shade", render: (value, shortValue) => shortValue || "-" },
      { dataKey: "fpo", title: "Fabric PO", render: (value, shortValue) => shortValue || "-" },
      { dataKey: "rollForm", title: "Roll Form" },
      {
        dataKey: "orderingWidth",
        title: "Ordering Width",
        align: "right",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.uomWidth]}`)
      },
      {
        dataKey: "grnQty",
        title: "GRN Qty",
        align: "right",
        render: (value, _, row) => (isEmpty(value) ? "" : `${toFixed(value)} ${UomCodeFromValue[row.uomLength]}`)
      },
      {
        dataKey: "associatedQty",
        title: "Associated Qty",
        inputType: "text-input",
        options: { type: "number" },
        align: "right",
        render: (value, _, row) => (isEmpty(value) ? "" : `${toFixed(value)} ${UomCodeFromValue[row.uomLength]}`)
      },
      {
        dataKey: "allocatedQty",
        title: "Allocated Qty",
        align: "right",
        render: (value, _, row) => (isEmpty(value) ? "" : `${toFixed(value)} ${UomCodeFromValue[row.uomLength]}`)
      }
    ];
    return columns.filter(c => fields.includes(c.dataKey));
  }, [invoiceFields, fabricFields, fabricItemFields, allWhList]);

  const defaultExpanded = true;

  return (
    <div>
      <div style={{ margin: "0 1.5em", marginTop: "1em" }}>
        <Typography variant="h5">Fabric - QC</Typography>
      </div>

      <div style={{ margin: "1.5em", marginTop: 0 }}>
        {/* Flow Details */}
        <div style={{ marginTop: "1.5em" }}>
          <MuiTable
            title={<CardHeader style={{ padding: 0 }} title="Order Details" />}
            columns={flowInfoColumns}
            rows={flowInfoRows}
            toolbar
            searchable
            pageable
            searchKeys={["style", "color"]}
            idKey="key"
            parentIdKey="parentKey"
            defaultExpanded={defaultExpanded}
            onRowClick={onRowClick}
            rowStyle={({ row }) => ({ backgroundColor: row?.key === selectedKey ? "#BFDDF8" : row?.key === parentKey ? "#C6D0DA" : "none" })}
            i18nKey="resources.orders.fields.flowInfo"
          />
        </div>

        {/* Invoices */}
        <div style={{ marginTop: "1.5em" }}>
          <MuiTable
            title={<CardHeader style={{ padding: 0 }} title="Invoices" />}
            columns={associationColumns}
            rows={associationRows}
            // editable={hasPrivilege(roles, hasAccess, ResourceId.LEDGER, Scope.UPDATE) && !disableAssociation}
            toolbar
            onSubmit={updateAssociation}
            i18nKey="resources.quality.fields.invoice"
          />
        </div>

        <RollInventory
          order={order}
          selectedRow={selectedRow}
          effPartName={part}
          flowInfoList={flowInfoList}
          ledgerList={ledgerList}
          rollList={rollList}
          conversionFactorList={conversionFactorList}
          roles={roles}
          hasAccess={hasAccess}
          onRefresh={onRefresh}
        />
      </div>
    </div>
  );
};

export default translate(FabricQualityView);
